import { useEffect, useState } from "react";
import ImageGallery from "react-image-gallery";

import Modal from "../../UI/Modal/Modal";
import CloseButton from "../../UI/CloseButton/CloseButton";
import DownloadIcon from "../../Icon-SVG/DownloadIcon";
import {
  renderLeftNav,
  renderPlayPauseButton,
  renderRightNav,
} from "../../PagesComponents/AnalysisReports/Screenshots/components/Controls";

import "./styles.scss";

const imageExtension = (src) => {
  const match = /^data:image\/(\w+)/.exec(src || "");
  return match ? match[1].replace("jpeg", "jpg") : "png";
};

/**
 * Large in-page viewer for screenshots, used instead of the browser's full
 * screen mode. images: [{ src, caption? }]. With more than one image it keeps
 * the gallery features (previous / next, slideshow, thumbnails, arrow keys).
 */
const ScreenshotViewer = ({
  show,
  onClose,
  images = [],
  startIndex = 0,
  title = "Screenshots",
}) => {
  const [index, setIndex] = useState(startIndex);
  const multiple = images.length > 1;

  useEffect(() => {
    if (show) setIndex(startIndex);
  }, [show, startIndex]);

  useEffect(() => {
    if (!show) return undefined;
    const keyHandler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", keyHandler);
    return () => window.removeEventListener("keydown", keyHandler);
  }, [show, onClose]);

  if (!show || images.length === 0) return null;

  const current = images[Math.min(index, images.length - 1)];

  const downloadHandler = () => {
    const link = document.createElement("a");
    link.href = current.src;
    link.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${index + 1}.${imageExtension(current.src)}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Modal
      active={show}
      setActive={(state, e) => {
        e?.stopPropagation();
        onClose();
      }}
      type="screenshot-viewer-modal"
    >
      <div
        className={`screenshot-viewer border ${multiple ? "" : "screenshot-viewer--single"}`}
        role="dialog"
        aria-label={title}
      >
        <header className="screenshot-viewer__header">
          <div className="screenshot-viewer__title">
            <h3>{title}</h3>
            {multiple && (
              <span className="screenshot-viewer__counter">
                {index + 1} / {images.length}
              </span>
            )}
            {current.caption && (
              <span className="screenshot-viewer__caption" title={current.caption}>
                {current.caption}
              </span>
            )}
          </div>
          <div className="screenshot-viewer__actions">
            <button
              type="button"
              className="screenshot-viewer__download"
              onClick={downloadHandler}
              title="Download this screenshot"
            >
              <DownloadIcon width={13} height={14} color="currentColor" />
              <span>Download</span>
            </button>
            <CloseButton onClick={onClose} />
          </div>
        </header>
        <div className="screenshot-viewer__body">
          {multiple ? (
            <ImageGallery
              items={images.map((image) => ({
                original: image.src,
                thumbnail: image.src,
                originalAlt: image.caption || title,
              }))}
              startIndex={startIndex}
              onSlide={setIndex}
              showFullscreenButton={false}
              showBullets={false}
              slideDuration={250}
              renderLeftNav={renderLeftNav}
              renderRightNav={renderRightNav}
              renderPlayPauseButton={renderPlayPauseButton}
            />
          ) : (
            <img src={current.src} alt={current.caption || title} />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ScreenshotViewer;
